import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowLeft,
    ShieldCheck,
    Briefcase,
    Cpu,
    ShoppingBag,
    FileText,
    CheckCircle2
} from 'lucide-react';

const ServicesPage = () => {

    // 简单的淡入动画效果 hook
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFDFD] text-slate-800 font-sans selection:bg-slate-200">

            {/* --- Header / Navigation Area --- */}
            <div className="pt-12 pb-6 px-6">
                <div className="container mx-auto max-w-6xl">
                    <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-8 group">
                        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium">返回首页</span>
                    </Link>

                    <div className="animate-fade-in-up">
                        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                            全方位业务体系
                        </h1>
                        <p className="text-lg text-slate-500 max-w-2xl">
                            从核心的合规财税，到技术驱动的运营支持。我们为您提供一站式企业服务解决方案。
                        </p>
                    </div>
                </div>
            </div>

            {/* --- Main Content Grid --- */}
            <div className="px-6 pb-24">
                <div className="container mx-auto max-w-6xl">

                    {/* 1. 核心许可项目 (Highlighted Card) */}
                    <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                        <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 md:p-12 shadow-xl hover:shadow-2xl transition-shadow duration-300 relative overflow-hidden group">
                            {/* 装饰背景 */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-slate-800 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-50 group-hover:opacity-70 transition-opacity"></div>

                            <div className="relative z-10 flex flex-col md:flex-row gap-8 md:items-start justify-between">
                                <div className="flex-1">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-200 text-xs font-bold uppercase tracking-wider mb-4 border border-yellow-500/30">
                                        <ShieldCheck size={14} />
                                        核心许可项目
                                    </div>
                                    <h2 className="text-3xl font-bold mb-4">代理记账与合规申报</h2>
                                    <p className="text-slate-400 leading-relaxed text-lg mb-6 max-w-xl">
                                        依法经相关部门批准。我们为企业提供全生命周期的账务处理、纳税申报及财税合规服务，确保您的经营安全无忧。
                                    </p>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-300">
                                        {['企业建账', '月度/季度纳税申报', '年度汇算清缴', '财税风险预警'].map((item, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <CheckCircle2 size={16} className="text-slate-500" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                                    <FileText size={32} className="text-white" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. 一般项目 Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>

                        {/* Card: 商务咨询 */}
                        <ServiceCard
                            icon={<Briefcase size={24} />}
                            title="商务咨询与秘书"
                            tags={['一般项目', '运营支持']}
                            description="助力企业高效运营，解决从注册落地到日常行政的各类难题。"
                            items={[
                                '财务咨询',
                                '商务秘书服务',
                                '社会经济咨询服务',
                                '信息咨询服务 (非许可类)'
                            ]}
                            delay="0.3s"
                        />

                        {/* Card: 技术服务 */}
                        <ServiceCard
                            icon={<Cpu size={24} />}
                            title="技术与数字化"
                            tags={['一般项目', '科技赋能']}
                            description="提供技术开发、转让及推广服务，通过科技中介连接创新资源。"
                            items={[
                                '技术开发与服务',
                                '技术咨询与交流',
                                '技术转让与推广',
                                '科技中介服务'
                            ]}
                            delay="0.4s"
                        />

                        {/* Card: 企业物资 (销售类) */}
                        <ServiceCard
                            icon={<ShoppingBag size={24} />}
                            title="企业采购与后勤"
                            tags={['一般项目', '物资支持']}
                            description="一站式解决企业办公所需的硬件与耗材，优化采购流程。"
                            items={[
                                '办公用品与日用百货',
                                '电子产品与五金产品',
                                '体育用品及器材'
                            ]}
                            delay="0.5s"
                            bgClass="bg-[#F8F9FA]" // 特殊一点的浅色背景
                        />
                    </div>

                </div>
            </div>

            {/* --- CTA Section --- */}
            <div className="bg-[#EEF2F6] py-20 px-6 mx-6 rounded-[3rem] mb-12 text-center">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                    不确定您的企业需要哪些服务？
                </h2>
                <p className="text-slate-500 mb-8 max-w-xl mx-auto">
                    我们随时待命，为您梳理需求，定制最适合您当前阶段的财税与运营方案。
                </p>
                <button className="px-8 py-4 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-transform hover:scale-105 shadow-lg">
                    预约咨询
                </button>
            </div>

            <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
        </div>
    );
};

// 提取出来的卡片组件，保持代码整洁
const ServiceCard = ({ icon, title, tags, description, items, delay, bgClass = "bg-white" }) => (
    <div
        className={`${bgClass} p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full`}
        style={{ animationDelay: delay }}
    >
        <div className="flex justify-between items-start mb-6">
            <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-800">
                {icon}
            </div>
            <div className="flex gap-2">
                {tags.map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold rounded-lg uppercase tracking-wide">
                        {tag}
                    </span>
                ))}
            </div>
        </div>

        <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
        <p className="text-slate-500 text-sm mb-6 leading-relaxed">
            {description}
        </p>

        <div className="mt-auto pt-6 border-t border-slate-100">
            <ul className="space-y-3">
                {items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 font-medium">
                        <div className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-1.5 shrink-0"></div>
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

export default ServicesPage;